/**
 * L'ASSISTANT « NOUVELLE SOIRÉE » — modèle pur (22/10/2026, contrat
 * `soirees-v1` §4.5 et §7.2).
 *
 * Un parcours, une décision par écran, dans la grammaire de l'ouverture
 * d'une soirée de la Main Courante (§9.6) : L'affiche, La soirée, Les salles,
 * Les tarifs (depuis les modèles), La mise en vente, Tout est prêt — puis
 * l'écran final. Chaque étape pose UNE question (`question`) et dit pourquoi
 * en une phrase (`sous`).
 *
 * Rien ne part avant « Tout est prêt » ; la soirée et ses tarifs partent en UN
 * envoi, toujours en brouillon. L'affiche (facultative, « Plus tard ») part
 * ENSUITE, par son propre envoi ; la mise en vente, s'il y a lieu, est un
 * troisième geste. Si l'un des deux échoue, l'écran final le dit et ne refait
 * que lui : la soirée n'est jamais recréée.
 *
 * Les heures de vente des modèles sont RELATIVES (« 02:30 dans la nuit », « à
 * l'ouverture », « 7 jours avant à 12:00 ») : l'assistant les pose sur la nuit
 * par arithmétique de CALENDRIER seulement — une heure avant 08:00 tombe le
 * lendemain — et ne calcule jamais de décalage horaire (principe 5). Tant que
 * personne n'y a touché, elles suivent la nuit et l'ouverture choisies.
 */

import { HEURE_RX, PARIS_RX, jourCivil, nomVide, parisLisible, plusJours, saisieDepuisBp, saisieDepuisCents, type ErreurBM, type Salle, type Stockage } from './soirees-maison-model.js'
import { lireAfficheBrouillon, type AfficheBrouillon } from './affiche-model.js'
import type { ChampASaisir, HeureRelative, ModeleTarif, Preferences } from './reglages-billetterie-model.js'
import {
  avertissementsSaisie, corpsTarif, saisieVierge,
  type CorpsTarif, type ErreursTarif, type SaisieTarif,
} from './tarif-saisie-model.js'

/** `titre` : le jalon ; `question` : le titre de l'étape ; `sous` : ce qu'elle décide, en une phrase. */
export const ETAPES = [
  { cle: 'affiche', titre: 'L’affiche', question: 'Quelle est l’affiche de la soirée ?', sous: 'Au format paysage de Shotgun (16:9). Vous pouvez aussi l’ajouter plus tard, depuis sa fiche.' },
  { cle: 'nuit', titre: 'La soirée', question: 'Comment s’appelle-t-elle, et quelle nuit ?', sous: 'Le nom affiché partout, la nuit et les horaires de la porte.' },
  { cle: 'salles', titre: 'Les salles', question: 'Lesquelles ouvrent cette nuit ?', sous: 'Et combien de personnes au plus, toutes salles comprises.' },
  { cle: 'tarifs', titre: 'Les tarifs', question: 'Que vend-on pour cette soirée ?', sous: 'Les modèles réglés dans Administration : cochez, réglez, ajoutez.' },
  { cle: 'ventes', titre: 'La mise en vente', question: 'Et ensuite, on la met en vente ?', sous: 'Rien ne se vend tant que vous ne l’avez pas décidé.' },
  { cle: 'recap', titre: 'Tout est prêt', question: 'Tout est prêt ?', sous: 'Vérifiez une dernière fois : la soirée se crée en un envoi.' },
] as const

export type CleEtape = (typeof ETAPES)[number]['cle']

export type ChoixVente = 'brouillon' | 'programmer' | 'maintenant'

export interface TarifAssistant {
  /** Identifiant local (jamais envoyé). */
  cle: string
  modele_id: string | null
  libelle: string
  retenu: boolean
  saisie: SaisieTarif
  /** Les heures du modèle, relatives à la nuit. */
  relatif: { vente_debut: HeureRelative; vente_fin: HeureRelative }
  /** Une heure retouchée à la main ne suit plus la nuit. */
  touche: { vente_debut: boolean; vente_fin: boolean }
  a_saisir: ChampASaisir[]
}

export interface SalleAssistant { code: string; nom: string; retenue: boolean; jauge: string }

export interface BrouillonAssistant {
  nom: string
  nuit: string
  ouverture: string
  fermeture: string
  salles: SalleAssistant[]
  jauge_totale: string
  famille: string
  presentation: string
  tarifs: TarifAssistant[]
  vente: ChoixVente
  /** Heure de Paris « AAAA-MM-JJTHH:MM » de la mise en vente programmée. */
  programmer_a: string
  /** L'affiche recadrée (§9.6), ou `null` : « Plus tard ». Elle part APRÈS la création. */
  affiche: AfficheBrouillon | null
}

/** « 02:30 dans la nuit du 24 » → « 2026-10-25T02:30 » : avant 08:00, c'est le lendemain civil. */
export function heureDeLaNuit(nuit: string, hhmm: string): string {
  const jour = Number(hhmm.slice(0, 2)) < 8 ? plusJours(nuit, 1) : nuit
  return `${jour}T${hhmm}`
}

/** Une heure relative posée sur la nuit choisie, en heure de Paris (§7.2). */
export function heureDepuisRelative(h: HeureRelative, nuit: string, ouverture: string): string | null {
  if (h === null || !jourCivil(nuit)) return null
  if ('heure_de_nuit' in h) return HEURE_RX.test(h.heure_de_nuit) ? heureDeLaNuit(nuit, h.heure_de_nuit) : null
  if ('a_l_ouverture' in h) return HEURE_RX.test(ouverture) ? heureDeLaNuit(nuit, ouverture) : null
  return HEURE_RX.test(h.heure) ? `${plusJours(nuit, -h.jours_avant)}T${h.heure}` : null
}

export function tarifDepuisModele(m: ModeleTarif, cle: string): TarifAssistant {
  const s = saisieVierge()
  return {
    cle, modele_id: m.id, libelle: m.libelle, retenu: m.coche,
    saisie: {
      ...s, nom: m.nom, canal: m.canal,
      prix: m.a_saisir.includes('prix_cents') || m.prix_cents === null ? '' : saisieDepuisCents(m.prix_cents),
      frais: m.frais_service_cents ? saisieDepuisCents(m.frais_service_cents) : '',
      tva: saisieDepuisBp(m.tva_bp),
      quota: m.a_saisir.includes('quota') || m.quota === null ? '' : String(m.quota),
      cascade_groupe: m.cascade_groupe ?? '', cascade_rang: m.cascade_rang === null ? '' : String(m.cascade_rang),
      entree_avant: m.entree_avant ?? '', exoneration: m.exoneration, cache: m.cache, consigne_porte: m.consigne_porte ?? '',
    },
    relatif: { vente_debut: m.vente_debut, vente_fin: m.vente_fin },
    touche: { vente_debut: false, vente_fin: false },
    a_saisir: [...m.a_saisir],
  }
}

export function tarifLibre(cle: string): TarifAssistant {
  return {
    cle, modele_id: null, libelle: 'Tarif libre', retenu: true, saisie: saisieVierge(),
    relatif: { vente_debut: null, vente_fin: null }, touche: { vente_debut: true, vente_fin: true }, a_saisir: [],
  }
}

/** Le départ : les réglages de Gestion pré-remplissent, la billetterie propose la nuit (§4.2). */
export function brouillonInitial(p: Preferences, salles: readonly Salle[], nuitProposee: string | null): BrouillonAssistant {
  const actives = salles.filter((s) => s.active)
  return {
    nom: '',
    nuit: nuitProposee ?? '',
    ouverture: p.nouvelle_soiree.ouverture,
    fermeture: p.nouvelle_soiree.fermeture,
    salles: actives.map((s) => ({ code: s.code, nom: s.nom, retenue: p.nouvelle_soiree.salles.includes(s.code), jauge: '' })),
    jauge_totale: p.nouvelle_soiree.jauge_totale === null ? '' : String(p.nouvelle_soiree.jauge_totale),
    famille: '',
    presentation: '',
    tarifs: p.modeles_tarifs.map((m, i) => tarifDepuisModele(m, `m${i}-${m.id}`)),
    vente: 'brouillon',
    programmer_a: '',
    affiche: null,
  }
}

/** La saisie d'un tarif avec ses heures posées sur la nuit (sauf celles retouchées). */
export function tarifEffectif(t: TarifAssistant, nuit: string, ouverture: string): SaisieTarif {
  return {
    ...t.saisie,
    vente_debut: t.touche.vente_debut ? t.saisie.vente_debut : heureDepuisRelative(t.relatif.vente_debut, nuit, ouverture) ?? '',
    vente_fin: t.touche.vente_fin ? t.saisie.vente_fin : heureDepuisRelative(t.relatif.vente_fin, nuit, ouverture) ?? '',
  }
}

export const retenus = (b: BrouillonAssistant): TarifAssistant[] => b.tarifs.filter((t) => t.retenu)

/** Durée « au mur » de la soirée, en minutes : une fermeture sur ou avant l'ouverture passe au lendemain (§3.3). */
export function dureeMinutes(ouverture: string, fermeture: string): number | null {
  if (!HEURE_RX.test(ouverture) || !HEURE_RX.test(fermeture)) return null
  const minutes = (h: string) => {
    const m = Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5))
    return Number(h.slice(0, 2)) < 8 ? m + 24 * 60 : m
  }
  const o = minutes(ouverture); let f = minutes(fermeture)
  if (f <= o) f += 24 * 60
  return f - o
}

export interface CorpsCreation {
  soiree: {
    nom: string; nuit: string; ouverture: string; fermeture: string; jauge_totale: number
    salles: { code: string; jauge: number | null }[]; famille: string | null; presentation: string | null
  }
  tarifs: CorpsTarif[]
}

/** Les erreurs qui BLOQUENT une étape (le bouton « Suivant » dit la première). */
export function erreursEtape(etape: CleEtape, b: BrouillonAssistant): string[] {
  const out: string[] = []
  if (etape === 'nuit' || etape === 'recap') {
    const nom = b.nom.trim()
    if (!nom || nom.length > 120) out.push('Donnez un nom à la soirée (120 caractères au plus).')
    else if (nomVide(nom)) out.push('Le nom de la soirée ne peut pas être fait seulement d’espaces ou de caractères invisibles.')
    if (!jourCivil(b.nuit)) out.push('Choisissez la nuit de la soirée.')
    if (!HEURE_RX.test(b.ouverture)) out.push('Heure d’ouverture illisible : par exemple « 23:55 ».')
    if (!HEURE_RX.test(b.fermeture)) out.push('Heure de fermeture illisible : par exemple « 06:00 ».')
    const d = dureeMinutes(b.ouverture, b.fermeture)
    if (d !== null && d > 16 * 60) out.push('Une soirée dure 16 h au plus.')
    // Famille et présentation se saisissent avec le nom (étape « La soirée »).
    if (b.famille.trim().length > 60) out.push('La famille tient en 60 caractères.')
    if (b.presentation.trim().length > 4000) out.push('La présentation tient en 4 000 caractères.')
  }
  if (etape === 'salles' || etape === 'recap') {
    const retenues = b.salles.filter((s) => s.retenue)
    if (retenues.length === 0) out.push('Ouvrez au moins une salle.')
    const jauge = Number(b.jauge_totale.trim())
    if (!/^\d{1,5}$/.test(b.jauge_totale.trim()) || jauge < 1 || jauge > 20_000) out.push('La jauge totale va de 1 à 20 000 personnes : c’est elle, et elle seule, qui limite les ventes.')
    for (const s of retenues) {
      const j = s.jauge.trim()
      if (j && (!/^\d{1,5}$/.test(j) || Number(j) < 1 || Number(j) > 20_000)) out.push(`Jauge de ${s.nom} : de 1 à 20 000, ou vide.`)
    }
  }
  if (etape === 'tarifs' || etape === 'recap') {
    const t = retenus(b)
    if (t.length > 40) out.push('40 tarifs au plus par soirée.')
    for (const [i, x] of t.entries()) {
      const e = erreursTarifAssistant(b, x)
      const premier = Object.values(e)[0]
      if (premier) out.push(`Tarif ${i + 1} (« ${x.saisie.nom.trim() || x.libelle} ») : ${premier}`)
    }
    const doublon = rangEnDouble(t)
    if (doublon) out.push(doublon)
  }
  if (etape === 'ventes' || etape === 'recap') {
    if (b.vente !== 'brouillon' && !retenus(b).length) out.push('Ajoutez au moins un tarif avant de mettre en vente : sinon, gardez la soirée en brouillon.')
    if (b.vente === 'programmer' && !PARIS_RX.test(b.programmer_a)) out.push('Choisissez le jour et l’heure de la mise en vente.')
  }
  return out
}

/** Les champs « à saisir » d'un modèle, puis les règles de saisie du tarif. */
export function erreursTarifAssistant(b: BrouillonAssistant, t: TarifAssistant): ErreursTarif {
  const e: ErreursTarif = {}
  if (t.a_saisir.includes('prix_cents') && !t.saisie.prix.trim()) e.prix = 'Prix à saisir pour ce tarif.'
  if (t.a_saisir.includes('quota') && !t.saisie.quota.trim()) e.quota = 'Quota à saisir : sans lui, ce tarif ne libère jamais le suivant de la cascade.'
  const r = corpsTarif(tarifEffectif(t, b.nuit, b.ouverture), 'creation')
  return 'erreurs' in r ? { ...r.erreurs, ...e } : e
}

/** §4.5 : deux tarifs du même groupe au même rang sont refusés avant toute écriture. */
export function rangEnDouble(t: readonly TarifAssistant[]): string | null {
  const vus = new Map<string, number>()
  for (const [i, x] of t.entries()) {
    const g = x.saisie.cascade_groupe.trim(); const r = x.saisie.cascade_rang.trim()
    if (!g || !r) continue
    const k = `${g}#${Number(r)}`
    const avant = vus.get(k)
    if (avant !== undefined) return `Tarif ${i + 1} (« ${x.saisie.nom.trim()} ») : le rang ${Number(r)} de la cascade « ${g} » est déjà pris par le tarif ${avant + 1}.`
    vus.set(k, i)
  }
  return null
}

/** Le corps de `POST /api/v1/billetterie/soirees` (§4.5) — l'ordre des tarifs est celui de l'écran. */
export function corpsCreation(b: BrouillonAssistant): { corps: CorpsCreation } | { erreurs: string[] } {
  const erreurs = erreursEtape('recap', b).filter((x) => !x.startsWith('Choisissez le jour') && !x.startsWith('Ajoutez au moins un tarif avant de mettre'))
  if (erreurs.length > 0) return { erreurs }
  const tarifs: CorpsTarif[] = []
  for (const t of retenus(b)) {
    const r = corpsTarif(tarifEffectif(t, b.nuit, b.ouverture), 'creation')
    if ('erreurs' in r) return { erreurs: Object.values(r.erreurs) }
    tarifs.push(r.corps)
  }
  return {
    corps: {
      soiree: {
        nom: b.nom.trim(), nuit: b.nuit, ouverture: b.ouverture, fermeture: b.fermeture,
        jauge_totale: Number(b.jauge_totale.trim()),
        salles: b.salles.filter((s) => s.retenue).map((s) => ({ code: s.code, jauge: s.jauge.trim() ? Number(s.jauge.trim()) : null })),
        famille: b.famille.trim() || null, presentation: b.presentation.trim() || null,
      },
      tarifs,
    },
  }
}

/** Les avertissements d'un tarif retenu, dans le contexte de sa cascade. */
export function avertissementsTarifAssistant(b: BrouillonAssistant, t: TarifAssistant): string[] {
  const g = t.saisie.cascade_groupe.trim(); const r = Number(t.saisie.cascade_rang.trim())
  const rangSuivant = !!g && retenus(b).some((x) => x.cle !== t.cle && x.saisie.cascade_groupe.trim() === g && Number(x.saisie.cascade_rang.trim()) > r)
  return avertissementsSaisie(tarifEffectif(t, b.nuit, b.ouverture), { rangSuivant })
}

/**
 * Quand la caisse commence-t-elle à vendre, si la soirée est mise en vente
 * maintenant (§5.1.2) ? D'après les tarifs SUR PLACE retenus et leur début de
 * vente posé sur la nuit (calendrier seul) : `'tout_de_suite'` si l'un n'a pas
 * de début ou l'a déjà passé, sinon le premier début (« AAAA-MM-JJTHH:MM »),
 * `null` sans tarif sur place. Dans une cascade, seul le premier rang compte :
 * les suivants attendent qu'il se libère.
 */
export function premiereVenteSurPlace(b: BrouillonAssistant, maintenant: string): 'tout_de_suite' | string | null {
  const premiers = new Map<string, { rang: number; debut: string }>()
  const libres: string[] = []
  for (const t of retenus(b)) {
    const s = tarifEffectif(t, b.nuit, b.ouverture)
    if (s.canal !== 'SUR_PLACE') continue
    const debut = s.vente_debut.trim()
    const g = s.cascade_groupe.trim()
    const rang = Number(s.cascade_rang.trim())
    if (!g || !Number.isFinite(rang)) { libres.push(debut); continue }
    const p = premiers.get(g)
    if (!p || rang < p.rang) premiers.set(g, { rang, debut })
  }
  const debuts = [...libres, ...[...premiers.values()].map((p) => p.debut)]
  if (debuts.length === 0) return null
  if (debuts.some((d) => !d || d.slice(0, 16) <= maintenant)) return 'tout_de_suite'
  return [...debuts].sort((x, y) => (x.slice(0, 16) < y.slice(0, 16) ? -1 : 1))[0]!
}

/** « Mettre en vente tout de suite » : ce que la caisse vendra, dit avant (option) et après (écran final). */
export function phraseVenteImmediate(premiere: 'tout_de_suite' | string | null, moment: 'option' | 'final'): string {
  const porte = ' ; les téléphones LC Porte ne la reçoivent qu’à partir de sa nuit.'
  if (premiere === 'tout_de_suite') {
    return moment === 'option' ? `Elle se vend aussitôt à la caisse de la page de secours, quelle que soit sa nuit${porte}` : 'Elle se vend dès maintenant à la caisse de la page de secours.'
  }
  if (premiere === null) {
    return moment === 'option' ? `Elle est en vente aussitôt, mais aucun tarif ne se vend à la porte${porte}` : 'Elle est en vente, mais aucun tarif ne se vend à la porte.'
  }
  const quand = parisLisible(premiere)
  return moment === 'option'
    ? `Elle est en vente aussitôt, mais aucun tarif sur place ne se vend avant le ${quand}${porte}`
    : `Elle est en vente, mais aucun tarif sur place ne se vend avant le ${quand} : d’ici là, la caisse de la page de secours ne vend rien.`
}

/** `RIEN_A_VENDRE` avant le clic : aucun tarif sur place retenu, la soirée serait en vente sans rien à vendre. */
export function rienAVendre(b: BrouillonAssistant): boolean {
  return b.vente !== 'brouillon' && !retenus(b).some((t) => t.saisie.canal === 'SUR_PLACE')
}

/** Un brouillon relu de `sessionStorage` : seulement s'il a la forme attendue. */
export function brouillonRelu(brut: string | null): BrouillonAssistant | null {
  if (!brut) return null
  try {
    const b = JSON.parse(brut) as Partial<BrouillonAssistant>
    if (typeof b.nom !== 'string' || typeof b.nuit !== 'string' || !Array.isArray(b.tarifs) || !Array.isArray(b.salles)) return null
    if (b.vente !== 'brouillon' && b.vente !== 'programmer' && b.vente !== 'maintenant') return null
    // Une affiche illisible (ou d'un brouillon d'avant l'affiche) est oubliée ; le reste est repris.
    return { ...(b as BrouillonAssistant), affiche: lireAfficheBrouillon(b.affiche) }
  } catch { return null }
}

/** Le brouillon a-t-il quelque chose qu'on regretterait de perdre ? */
export function brouillonEntame(b: BrouillonAssistant): boolean {
  return b.nom.trim() !== '' || b.famille.trim() !== '' || b.presentation.trim() !== '' || b.affiche !== null
}

/**
 * Range le brouillon dans l'onglet (`sessionStorage`). L'affiche y est en
 * data URL : si l'ensemble ne tient pas (quota), le brouillon est rangé SANS
 * elle — et l'écran le dit : un rechargement perdrait l'affiche, pas le reste.
 */
export type RangementBrouillon = 'complet' | 'sans_affiche' | 'impossible'

export function rangerBrouillon(s: Stockage, cle: string, b: BrouillonAssistant): RangementBrouillon {
  try { s.setItem(cle, JSON.stringify(b)); return 'complet' } catch { /* trop gros : on retente sans l'image */ }
  if (b.affiche) {
    try { s.setItem(cle, JSON.stringify({ ...b, affiche: null })); return 'sans_affiche' } catch { /* plein, même sans elle */ }
  }
  return 'impossible'
}

export const PHRASE_RANGEMENT: Record<Exclude<RangementBrouillon, 'complet'>, string> = {
  sans_affiche: 'L’affiche est trop lourde pour être gardée dans cet onglet : le reste du brouillon l’est. Si vous rechargez la page avant de créer la soirée, il faudra la redéposer.',
  impossible: 'Ce brouillon ne peut pas être gardé dans cet onglet : il vit le temps de la fenêtre. Ne rechargez pas la page avant d’avoir créé la soirée.',
}

/** La première étape qui bloque la création (les jalons permettent de sauter des étapes sans les valider). */
export function premiereEtapeBloquante(b: BrouillonAssistant): CleEtape | null {
  for (const e of ['nuit', 'salles', 'tarifs', 'ventes'] as const) if (erreursEtape(e, b).length > 0) return e
  return null
}

/* ═══════════════════════ la création, puis l'affiche ═══════════════════════ */

type Issue<T> = { ok: true; data: T } | { ok: false; erreur: ErreurBM }

export interface EtatAffiche { etat: 'sans' | 'envoi' | 'faite' | 'echec'; erreur: ErreurBM | null }

export const PHRASE_AFFICHE_NON_ENVOYEE = 'Soirée créée. L’affiche n’a pas été envoyée.'

/**
 * La soirée d'abord (`POST /soirees`, inchangé), puis l'affiche (§9.6), par
 * son propre envoi et sa propre clé. L'affiche n'est envoyée QUE si la soirée
 * est créée ; son échec ne défait rien. `quandCreee` est appelé dès la
 * création lue — avant l'affiche : le brouillon s'oublie, l'écran final
 * s'affiche, même si l'affiche tarde.
 */
export async function creerPuisEnvoyerAffiche<C extends { soiree_id: string }>(o: {
  creer: () => Promise<Issue<C>>
  envoyerAffiche: ((soireeId: string) => Promise<Issue<unknown>>) | null
  quandCreee?: ((c: C) => void) | undefined
}): Promise<{ creation: Issue<C>; affiche: EtatAffiche }> {
  const creation = await o.creer()
  if (!creation.ok) return { creation, affiche: { etat: 'sans', erreur: null } }
  o.quandCreee?.(creation.data)
  if (!o.envoyerAffiche) return { creation, affiche: { etat: 'sans', erreur: null } }
  return { creation, affiche: await envoyerAfficheSeule(() => o.envoyerAffiche!(creation.data.soiree_id)) }
}

/** L'affiche seule : à la création, puis à chaque « Réessayer » — jamais la soirée. */
export async function envoyerAfficheSeule(envoi: () => Promise<Issue<unknown>>): Promise<EtatAffiche> {
  const r = await envoi()
  return r.ok ? { etat: 'faite', erreur: null } : { etat: 'echec', erreur: r.erreur }
}
